import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  ShieldCheck,
  AlertCircle,
  RefreshCcw,
  Search,
  ChevronRight,
  Filter,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { useReceipts } from "../../lib/receipts/useReceipts";
import { useSubscriptions } from "../../lib/subscriptions/useSubscriptions";
import type { Expense } from "../../lib/expenses/types";
import type { Invoice } from "../../lib/invoices/types";
import type { Subscription } from "../../lib/subscriptions/types";
import { formatAmountCents, formatIsoDate } from "../../lib/format";
import { ScopePinnedBadge } from "../components/ScopePinnedBadge";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { useEffectiveScopeForTab } from "../../lib/scope";

// ---- Scoring formula ------------------------------------------------------
// Overall audit score = 100 - (20 * missingReceiptsPct) - (20 * overdueInvoicesPct)
// clamped to [0, 100].
//   missingReceiptsPct = expensesWithoutReceipt / totalExpenses (0 when no expenses)
//   overdueInvoicesPct = overdueInvoices / totalInvoices (0 when no invoices)
// Empty state => score = 100.
function computeAuditScore(
  expenses: Expense[],
  invoices: Invoice[],
  receipts: { linkedExpenseId?: string }[],
): number {
  const totalExpenses = expenses.length;
  const totalInvoices = invoices.length;

  const missingReceipts = expenses.filter(
    (e) => !receipts.some((r) => r.linkedExpenseId === e.id),
  ).length;
  const overdueInvoices = invoices.filter((i) => i.status === "overdue").length;

  const missingReceiptsPct =
    totalExpenses === 0 ? 0 : missingReceipts / totalExpenses;
  const overdueInvoicesPct =
    totalInvoices === 0 ? 0 : overdueInvoices / totalInvoices;

  const raw = 100 - 20 * missingReceiptsPct - 20 * overdueInvoicesPct;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

type ActionableItem =
  | { kind: "expense"; id: string; data: Expense; updatedAt: string }
  | { kind: "invoice"; id: string; data: Invoice; updatedAt: string }
  | {
      kind: "subscription";
      id: string;
      data: Subscription;
      updatedAt: string;
    };

function actionableSortKey(item: ActionableItem): string {
  return item.updatedAt;
}

export function AuditDashboard() {
  const navigate = useNavigate();
  const { expenses } = useExpenses();
  const { invoices } = useInvoices();
  const { receipts } = useReceipts();
  const { subscriptions } = useSubscriptions();
  const { can } = useCapabilities();
  // Audit Dashboard is company-only: TAB_SCOPE_SUPPORT.audit_dashboard says
  // supportsMine=false, fallback='company'. If the global scope is 'mine',
  // pinned will be true and we render the ScopePinnedBadge near the title.
  const { scope, pinned } = useEffectiveScopeForTab("audit_dashboard");
  const hasRead = can("read:audit_dashboard");

  const missingReceipts = useMemo(
    () =>
      expenses.filter(
        (e) => !receipts.some((r) => r.linkedExpenseId === e.id),
      ),
    [expenses, receipts],
  );
  const overdueInvoices = useMemo(
    () => invoices.filter((i) => i.status === "overdue"),
    [invoices],
  );
  const needsInfoExpenses = useMemo(
    () => expenses.filter((e) => e.status === "needs_info"),
    [expenses],
  );
  const subscriptionWarnings = useMemo(
    () => subscriptions.filter((s) => s.status === "warning"),
    [subscriptions],
  );
  // Policy flags is a computable proxy for the AI-powered "Tax Policy Flags":
  // count of expenses with no taxPolicyId set. Anything more sophisticated
  // requires the AI pipeline.
  const policyFlaggedExpenses = useMemo(
    () => expenses.filter((e) => !e.taxPolicyId),
    [expenses],
  );

  const score = useMemo(
    () => computeAuditScore(expenses, invoices, receipts),
    [expenses, invoices, receipts],
  );

  const [simOpen, setSimOpen] = useState(false);

  const pieData = [
    { name: "Ready", value: score, color: "#3B82F6" },
    { name: "Gap", value: Math.max(0, 100 - score), color: "#E2E8F0" },
  ];

  const actionableItems = useMemo<ActionableItem[]>(() => {
    const topExpenses = [...needsInfoExpenses]
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      .slice(0, 3)
      .map<ActionableItem>((e) => ({
        kind: "expense",
        id: e.id,
        data: e,
        updatedAt: e.updatedAt,
      }));
    const topInvoices = [...overdueInvoices]
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      .slice(0, 3)
      .map<ActionableItem>((i) => ({
        kind: "invoice",
        id: i.id,
        data: i,
        updatedAt: i.updatedAt,
      }));
    const topSubs = [...subscriptionWarnings]
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      .slice(0, 2)
      .map<ActionableItem>((s) => ({
        kind: "subscription",
        id: s.id,
        data: s,
        updatedAt: s.updatedAt,
      }));
    return [...topExpenses, ...topInvoices, ...topSubs]
      .sort((a, b) => (actionableSortKey(a) < actionableSortKey(b) ? 1 : -1))
      .slice(0, 8);
  }, [needsInfoExpenses, overdueInvoices, subscriptionWarnings]);

  const simRisks = useMemo(() => {
    const items: Array<{
      severity: "high" | "medium";
      label: string;
      action: () => void;
    }> = [];

    const bigMissingReceipts = expenses.filter(
      (e) => e.amountCents > 2500 && !receipts.some((r) => r.linkedExpenseId === e.id),
    );
    if (bigMissingReceipts.length > 0) {
      items.push({
        severity: "high",
        label: `${bigMissingReceipts.length} expense${bigMissingReceipts.length !== 1 ? "s" : ""} over $25 missing receipts`,
        action: () => navigate("/expenses?filter=missingReceipt"),
      });
    }

    const undocumentedMeals = expenses.filter((e) => {
      const cat = e.categoryLabel?.toLowerCase() ?? "";
      return (cat.includes("meal") || cat.includes("entertainment")) && !e.businessPurpose?.trim();
    });
    if (undocumentedMeals.length > 0) {
      items.push({
        severity: "medium",
        label: `${undocumentedMeals.length} meal/entertainment expense${undocumentedMeals.length !== 1 ? "s" : ""} lack business justification`,
        action: () => navigate("/inbox"),
      });
    }

    const noPolicy = expenses.filter((e) => !e.taxPolicyId);
    if (noPolicy.length > 0) {
      items.push({
        severity: "medium",
        label: `${noPolicy.length} expense${noPolicy.length !== 1 ? "s" : ""} have no tax policy assigned`,
        action: () => navigate("/policies"),
      });
    }

    const stuckNeedsInfo = expenses.filter((e) => e.status === "needs_info");
    if (stuckNeedsInfo.length > 0) {
      items.push({
        severity: "high",
        label: `${stuckNeedsInfo.length} item${stuckNeedsInfo.length !== 1 ? "s" : ""} flagged for more information`,
        action: () => navigate("/inbox"),
      });
    }

    return items.sort((a, b) => (a.severity === "high" && b.severity !== "high" ? -1 : 1));
  }, [expenses, receipts, navigate]);

  function handleActionableClick(item: ActionableItem): void {
    if (item.kind === "expense") {
      navigate(`/expenses/${item.id}`);
    } else if (item.kind === "invoice") {
      navigate(`/invoices`);
    } else {
      navigate(`/expenses?tab=subscriptions`);
    }
  }

  // Page-level cap gate. Placed after all hooks so their ordering never
  // changes between renders (React rule-of-hooks). Employees without
  // read:audit_dashboard see a terse denied panel instead of the dashboard;
  // auditor / accountant / admin / owner have it by default.
  if (!hasRead) {
    return (
      <div
        role="alert"
        data-testid="page-access-denied"
        className="max-w-md mx-auto mt-12 bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center"
      >
        <h1 className="text-lg font-semibold text-slate-900">
          You don't have access to the Audit Dashboard
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Ask an admin to grant you the <code>read:audit_dashboard</code>{" "}
          capability.
        </p>
      </div>
    );
  }

  return (
    <div
      className="h-full flex flex-col gap-6 max-w-6xl mx-auto overflow-y-auto pb-6"
      data-scope={scope}
      data-testid="audit-dashboard-root"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">Audit Dashboard</h1>
            {pinned && <ScopePinnedBadge forcedScope={scope} />}
          </div>
          <p className="text-slate-500 mt-1">
            Real-time health of your expense reports
          </p>
        </div>
        <button
          type="button"
          disabled
          aria-disabled
          title="Date range filtering coming soon"
          className="flex items-center gap-2 px-4 py-2 text-slate-400 bg-white border border-slate-200 font-medium rounded-xl shadow-sm text-sm cursor-not-allowed"
        >
          <Filter className="w-4 h-4" /> All time
        </button>
      </div>

      {/* Top Row: Score & Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div
          data-testid="audit-score-pie"
          className="col-span-1 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col items-center justify-center"
        >
          <h3 className="font-semibold text-slate-900 mb-2">
            Overall Audit Score
          </h3>
          {/* pointer-events: none on the chart wrapper because the recharts
              ResponsiveContainer inserts an overflowing wrapper div that
              can intercept clicks targeted at cards further down the page
              (Playwright's default click then bails because the "blocker"
              is a different element). The chart is purely decorative, so
              dropping pointer events is safe. */}
          <div className="w-40 h-40 relative pointer-events-none">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  stroke="none"
                  isAnimationActive={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span
                data-testid="audit-score-value"
                className="text-3xl font-bold text-slate-900"
              >
                {score}%
              </span>
              <span className="text-xs text-slate-500 font-medium uppercase tracking-wider mt-1">
                Ready
              </span>
            </div>
          </div>
          <div className="mt-4 text-xs text-slate-500 text-center max-w-[220px]">
            Score = 100 − 20 × (missing receipts %) − 20 × (overdue invoices %)
          </div>
        </div>

        <div className="col-span-2 grid grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 font-medium text-sm">
                  Missing Receipts
                </p>
                <h4
                  data-testid="audit-missing-receipts-count"
                  className="text-3xl font-bold text-slate-900 mt-2"
                >
                  {missingReceipts.length}
                </h4>
              </div>
              <div className="p-3 bg-red-50 text-red-600 rounded-xl border border-red-100">
                <AlertCircle className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-6">
              <button
                type="button"
                data-testid="audit-view-missing-receipts"
                onClick={() => navigate("/expenses?filter=missingReceipt")}
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                View missing items <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 font-medium text-sm">
                  Overdue Invoices
                </p>
                <h4
                  data-testid="audit-overdue-invoices-count"
                  className="text-3xl font-bold text-slate-900 mt-2"
                >
                  {overdueInvoices.length}
                </h4>
              </div>
              <div className="p-3 bg-amber-50 text-amber-600 rounded-xl border border-amber-100">
                <RefreshCcw className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-6">
              <button
                type="button"
                data-testid="audit-review-overdue-invoices"
                onClick={() => navigate("/invoices")}
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Review invoices <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 font-medium text-sm">
                  Expenses needing info
                </p>
                <h4
                  data-testid="audit-needs-info-count"
                  className="text-3xl font-bold text-slate-900 mt-2"
                >
                  {needsInfoExpenses.length}
                </h4>
              </div>
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl border border-blue-100">
                <Search className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-6">
              <button
                type="button"
                data-testid="audit-resolve-needs-info"
                onClick={() => navigate("/inbox")}
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Resolve items <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 font-medium text-sm">
                  Subscription warnings
                </p>
                <h4
                  data-testid="audit-subscription-warnings-count"
                  className="text-3xl font-bold text-slate-900 mt-2"
                >
                  {subscriptionWarnings.length}
                </h4>
              </div>
              <div className="p-3 bg-amber-50 text-amber-600 rounded-xl border border-amber-100">
                <RefreshCcw className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-6">
              <button
                type="button"
                data-testid="audit-review-subscriptions"
                onClick={() =>
                  navigate("/expenses?tab=subscriptions&filter=warning")
                }
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Review subscriptions <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Second row: AI-dependent tiles. Kept visible (per plan) with a
          "coming soon" hint so users understand the gap without seeing fake
          numbers. Low Confidence is fully AI-gated; Tax Policy Flags is
          approximated with "expenses with no policy assigned". */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between opacity-75">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-slate-500 font-medium text-sm flex items-center gap-1.5">
                Low Confidence Items
                <Sparkles
                  className="w-3.5 h-3.5 text-slate-400"
                  aria-label="AI-powered"
                />
              </p>
              <h4
                data-testid="audit-low-confidence-count"
                className="text-3xl font-bold text-slate-400 mt-2"
                title="Requires AI confidence scoring — coming soon"
              >
                —
              </h4>
            </div>
            <div className="p-3 bg-slate-50 text-slate-400 rounded-xl border border-slate-100">
              <Search className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-6 text-xs text-slate-500">
            Requires AI confidence scoring — coming soon
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-slate-500 font-medium text-sm">
                Tax Policy Flags
              </p>
              <h4
                data-testid="audit-policy-flags-count"
                className="text-3xl font-bold text-slate-900 mt-2"
              >
                {policyFlaggedExpenses.length}
              </h4>
              <p className="text-xs text-slate-500 mt-1">
                Expenses without an assigned tax policy
              </p>
            </div>
            <div className="p-3 bg-purple-50 text-purple-600 rounded-xl border border-purple-100">
              <ShieldCheck className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-6">
            <button
              type="button"
              data-testid="audit-review-policies"
              onClick={() => navigate("/policies")}
              className="text-sm font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1"
            >
              Review deductibility <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Actionable List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" /> Require Attention
          </h3>
          <button
            type="button"
            data-testid="audit-view-all"
            onClick={() => navigate("/expenses")}
            className="text-sm font-medium text-slate-500 hover:text-slate-700"
          >
            View All
          </button>
        </div>
        {actionableItems.length === 0 ? (
          <div
            data-testid="audit-actionable-empty"
            className="p-8 text-center text-sm text-slate-500"
          >
            Nothing needs attention. Great work!
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {actionableItems.map((item) => {
              const vendor =
                item.kind === "expense"
                  ? item.data.vendor
                  : item.kind === "invoice"
                    ? item.data.counterparty
                    : item.data.vendor;
              const issue =
                item.kind === "expense"
                  ? "Missing information"
                  : item.kind === "invoice"
                    ? "Invoice overdue"
                    : "Subscription flagged";
              const amountCents =
                item.kind === "subscription"
                  ? (item.data.lastChargeCents ?? 0)
                  : item.data.amountCents;
              const date =
                item.kind === "expense"
                  ? item.data.occurredOn
                  : item.kind === "invoice"
                    ? item.data.issuedOn
                    : (item.data.lastChargeOn ?? item.data.updatedAt);
              const dot =
                item.kind === "expense"
                  ? "bg-blue-500"
                  : item.kind === "invoice"
                    ? "bg-amber-500"
                    : "bg-purple-500";
              const currency =
                item.kind === "subscription" ? "USD" : item.data.currency;
              return (
                <button
                  key={`${item.kind}-${item.id}`}
                  type="button"
                  data-testid={`audit-actionable-row-${item.id}`}
                  onClick={() => handleActionableClick(item)}
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-2 rounded-full ${dot}`} />
                    <div>
                      <h4 className="font-semibold text-slate-900">
                        {vendor}
                      </h4>
                      <p className="text-sm text-slate-500">
                        {issue} • {formatIsoDate(date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="font-semibold text-slate-900">
                      {formatAmountCents(amountCents, currency)}
                    </span>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Audit Simulation */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" /> Audit Simulation
          </h3>
          <button
            type="button"
            onClick={() => setSimOpen((v) => !v)}
            className="text-sm font-medium px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
          >
            {simOpen ? "Close" : "Find weak spots"}
          </button>
        </div>
        {simOpen && (
          <div className="p-6 flex flex-col gap-3">
            {simRisks.length === 0 ? (
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-100 text-green-800 text-sm font-medium">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                No audit risks detected. Your records look clean.
              </div>
            ) : (
              <>
                {simRisks.map((risk, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-200 bg-white"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                          risk.severity === "high" ? "bg-red-500" : "bg-amber-400"
                        }`}
                      />
                      <span className="text-sm text-slate-700">{risk.label}</span>
                    </div>
                    <button
                      type="button"
                      onClick={risk.action}
                      className="text-sm font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap flex items-center gap-1"
                    >
                      Fix it <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => navigate("/exports")}
                    className="text-sm font-medium text-slate-500 hover:text-slate-700 flex items-center gap-1"
                  >
                    Export audit report <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
